/**
 * Web Content Slide
 * Embeds external websites via a server-side proxy that strips
 * X-Frame-Options / CSP frame-ancestors headers.
 *
 * The proxy is a Supabase Edge Function that requires an Authorization
 * header. Because iframes cannot set custom headers, we fetch the HTML
 * via JavaScript (with the anon key) and inject it into the iframe
 * using a blob URL.
 *
 * For sites that use JS-based frame-busting or require login,
 * a prominent fallback with "Open in new tab" is shown.
 */

import { useEffect, useRef, useState } from 'react';
import type { ContentSlideProps } from '../types';
import { projectId, publicAnonKey } from '../../../../utils/supabase/info';
import { stripHtml } from '../../../../utils/slides/stripHtml';

const PROXY_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-9d7d881a/web-proxy`;

function buildProxyUrl(targetUrl: string): string {
  return `${PROXY_BASE}?url=${encodeURIComponent(targetUrl)}`;
}

/**
 * Some domains are known to aggressively block iframe embedding
 * (JS frame-busting, login walls, etc.). Detect early and skip the proxy.
 */
const NON_EMBEDDABLE_HOSTS = [
  'accounts.google.com',
  'mail.google.com',
  'drive.google.com',
  'facebook.com',
  'www.facebook.com',
  'twitter.com',
  'x.com',
  'instagram.com',
  'www.instagram.com',
  'linkedin.com',
  'www.linkedin.com',
  'github.com',
  'login.microsoftonline.com',
  'outlook.live.com',
  'outlook.office.com',
];

function isKnownNonEmbeddable(url: string): boolean {
  try {
    const hostname = new URL(url).hostname;
    return NON_EMBEDDABLE_HOSTS.some(
      (h) => hostname === h || hostname.endsWith(`.${h}`)
    );
  } catch {
    return false;
  }
}

export default function WebContentSlide({ slide, className = '' }: ContentSlideProps) {
  const { content } = slide;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const nonEmbeddable = content?.url ? isKnownNonEmbeddable(content.url) : false;

  // Fetch the proxied HTML with the Authorization header (required by
  // Supabase Edge Functions) and create a blob URL for the iframe.
  useEffect(() => {
    if (!content?.url || nonEmbeddable) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15_000);

    (async () => {
      try {
        const proxyUrl = buildProxyUrl(content.url);
        const res = await fetch(proxyUrl, {
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
          },
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`Proxy returned ${res.status}`);

        const contentType = res.headers.get('content-type') || 'text/html';
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(
          new Blob([blob], { type: contentType })
        );

        if (!cancelled) {
          setBlobUrl(objectUrl);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setLoading(false);
          setError(true);
        }
      } finally {
        clearTimeout(timeout);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [content?.url, nonEmbeddable]);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!content?.url) {
    return (
      <div className={`mb-8 max-w-3xl mx-auto ${className}`}>
        <p className="text-muted-foreground text-center">No URL provided.</p>
      </div>
    );
  }

  const showFallback = error || nonEmbeddable;

  return (
    <div className={`mb-8 max-w-3xl mx-auto ${className}`}>
      {content?.showTitle !== false && content?.title && (
        <h2 className="text-3xl font-bold mb-4 text-center">
          {stripHtml(content.title)}
        </h2>
      )}
      {content?.description && (
        <p className="text-lg mb-4 text-foreground">{stripHtml(content.description)}</p>
      )}

      {showFallback ? (
        <div className="w-full h-[50vh] min-h-[280px] max-h-[70vh] rounded-2xl border-2 border-border shadow-lg bg-muted flex flex-col items-center justify-center gap-4 px-6 text-center">
          <div className="text-4xl">⚠️</div>
          <p className="text-lg font-semibold text-foreground">Could not load this page</p>
          <p className="text-sm text-muted-foreground max-w-md">
            {nonEmbeddable
              ? 'This website does not allow embedding. You can open it directly in a new tab.'
              : 'The site may be unavailable, require a login, or block embedding.'}
          </p>
          <a
            href={content.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Open in new tab ↗
          </a>
        </div>
      ) : (
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 rounded-2xl bg-muted flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading…</p>
              </div>
            </div>
          )}
          {blobUrl && (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              className="w-full h-[60vh] min-h-[320px] max-h-[75vh] rounded-2xl border-2 border-border shadow-lg"
              title="Web content"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          )}
          <div className="mt-2 text-right">
            <a
              href={content.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            >
              Open in new tab ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
